/**
 * External alert settings — the wire contract between the API worker, the
 * desktop app, and the portal.
 *
 * All three surfaces edit the same per-account settings, so the shapes here
 * are the single source of truth: the API stores and enforces them, the two
 * UIs render them, and the desktop's Rust mirror is pinned to this file by a
 * serde test. Change something here and every consumer moves together.
 */

export type AlertChannelKind = "email" | "slack" | "discord" | "webhook";

/** The channels that deliver via a user-supplied webhook URL. */
export type WebhookChannelKind = Exclude<AlertChannelKind, "email">;

export const WEBHOOK_CHANNEL_KINDS: readonly WebhookChannelKind[] = [
  "slack",
  "discord",
  "webhook",
];

export const ALL_CHANNEL_KINDS: readonly AlertChannelKind[] = [
  "email",
  "slack",
  "discord",
  "webhook",
];

/**
 * How often a channel receives a status summary — the periodic "here is how
 * your backups are doing" message, as opposed to the alerts, which are silent
 * while everything works. `off` is a real choice a user makes; it is not what
 * an absent field means (see `normalizeNotificationSettings`).
 */
export type SummaryCadence = "off" | "daily" | "weekly" | "monthly";

export const SUMMARY_CADENCES: readonly SummaryCadence[] = ["off", "daily", "weekly", "monthly"];

export interface ChannelSettings {
  readonly enabled: boolean;
  /**
   * Minimum days between sends on this channel. 0 means instant, which only
   * the webhook kinds may choose: a webhook message costs nothing, while
   * every email spends real money, so email's floor is 1.
   */
  readonly minIntervalDays: number;
  /** Required (and validated) for the webhook kinds; never present for email. */
  readonly webhookUrl?: string;
  /**
   * Webhook kinds only, and honored only while `minIntervalDays` is 0. A
   * success ping is a heartbeat; delivered days late it is only noise, so
   * digest-mode channels report failures alone.
   */
  readonly notifyOnSuccess?: boolean;
  /**
   * How often this channel gets a status summary. Sent only while `enabled`,
   * so a user who never turned a channel on gets nothing regardless of what
   * this says.
   *
   * Optional on the wire, and *only* there: a desktop build older than the
   * field serialises `ChannelSettings` without it, and reading that absence as
   * a value would silently overwrite whatever the user had chosen. The API
   * substitutes the stored cadence before normalising, so everything past that
   * point sees a real one.
   */
  readonly summaryCadence?: SummaryCadence;
}

export interface NotificationSettings {
  /**
   * Days of grace past a plan's own cadence before the server alerts that
   * backups have gone stale. This drives email/webhook alerts; the desktop
   * keeps a separate local value for its in-app banner.
   */
  readonly staleAfterDays: number;
  /**
   * IANA zone the summary schedule is computed in — which local day a period
   * covers, and when 07:00 has arrived. Null means UTC.
   *
   * Written as a side effect of Save by whichever UI saved (both run in a
   * browser engine and both have `Intl`), never shown and never edited: a
   * timezone picker is a question with an answer the machine already knows.
   */
  readonly timeZone?: string | null;
  readonly channels: Readonly<Record<AlertChannelKind, ChannelSettings>>;
}

/**
 * A per-plan override. Null means "inherit the global setting"; an override
 * that is all nulls is equivalent to having none, and the API deletes it
 * rather than storing it.
 */
export interface PlanNotificationOverride {
  readonly alertOnFailure: boolean | null;
  /**
   * Inherits `ChannelSettings.notifyOnSuccess`, and inherits its instant-only
   * rule with it: a channel in digest mode reports failures and staleness
   * alone, so pinning this to true has no effect there. On an account whose
   * only channel is email — whose floor is a 1-day interval — it can never
   * send anything, which is why both editors say so beside the control.
   */
  readonly alertOnSuccess: boolean | null;
  readonly staleAfterDays: number | null;
}

// WHATWG `URL` exists in every consumer runtime (browsers, workers, node),
// but this package's lib floor is ES-only, so declare the sliver used here.
declare const URL: new (url: string) => { protocol: string; hostname: string };

// Same reason, for the one `Intl` call below. Every consumer runtime has it —
// workerd included, which is what lets the cron compute a local calendar day.
declare const Intl: {
  DateTimeFormat: new (locales?: string, options?: { timeZone?: string }) => unknown;
};

/** An IANA zone name long enough for anything real (`America/Argentina/Buenos_Aires`
 * is 31) and short enough not to be a place to smuggle a payload. */
export const MAX_TIME_ZONE_LENGTH = 64;

export const MIN_STALE_AFTER_DAYS = 1;
export const MAX_STALE_AFTER_DAYS = 365;
export const MAX_INTERVAL_DAYS = 365;
export const MAX_WEBHOOK_URL_LENGTH = 2048;

/**
 * Everything off. A user has no settings row until they first save, and
 * "absent" must mean "sends nothing" — alerts are strictly opt-in.
 */
export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  staleAfterDays: 3,
  timeZone: null,
  channels: {
    email: { enabled: false, minIntervalDays: 1, summaryCadence: "weekly" },
    slack: { enabled: false, minIntervalDays: 0, webhookUrl: "", notifyOnSuccess: false, summaryCadence: "weekly" },
    discord: { enabled: false, minIntervalDays: 0, webhookUrl: "", notifyOnSuccess: false, summaryCadence: "weekly" },
    webhook: { enabled: false, minIntervalDays: 0, webhookUrl: "", notifyOnSuccess: false, summaryCadence: "weekly" },
  },
};

export const INHERIT_PLAN_OVERRIDE: PlanNotificationOverride = {
  alertOnFailure: null,
  alertOnSuccess: null,
  staleAfterDays: null,
};

/**
 * Why a webhook URL cannot be accepted, or null when it can.
 *
 * https-only and no loopback/private-range hosts: the URL is fetched by our
 * server, so a permissive field here is a request forgery invitation. The
 * check runs at save time in every UI and again at send time in the worker.
 */
export function webhookUrlProblem(url: string): string | null {
  if (url.length === 0) return "Enter a webhook URL.";
  if (url.length > MAX_WEBHOOK_URL_LENGTH) {
    return "That URL is too long to be a webhook.";
  }

  let protocol: string;
  let host: string;
  try {
    const parsed = new URL(url);
    protocol = parsed.protocol;
    host = parsed.hostname.toLowerCase();
  } catch {
    return "That doesn't look like a valid URL.";
  }

  if (protocol !== "https:") return "Webhook URLs must use https.";
  if (isPrivateHost(host)) return "Webhook URLs must point at a public host.";

  return null;
}

/**
 * Hosts our server must never be asked to fetch.
 *
 * Both families need spelling out, because `URL` normalizes IPv6 into a form
 * the dotted-quad patterns cannot see: `[::ffff:127.0.0.1]` arrives here as
 * `[::ffff:7f00:1]`, which matches no IPv4 rule. Rather than chase each
 * encoding, every IPv4-mapped address is refused outright — a real webhook
 * provider has no reason to be addressed that way — alongside the unspecified,
 * loopback, unique-local and link-local IPv6 ranges.
 */
function isPrivateHost(rawHost: string): boolean {
  // A fully-qualified name may end in a root dot, and `URL` keeps it:
  // `https://localhost./hook` arrives here as `localhost.`, which matched
  // neither the equality nor the suffix test and sailed straight through. DNS
  // resolves it to exactly the same place, so strip it before deciding.
  const host = rawHost.endsWith(".") ? rawHost.slice(0, -1) : rawHost;

  if (host === "localhost" || host.endsWith(".localhost")) return true;

  if (
    /^127\.\d+\.\d+\.\d+$/.test(host) ||
    /^0\.\d+\.\d+\.\d+$/.test(host) ||
    /^10\.\d+\.\d+\.\d+$/.test(host) ||
    /^192\.168\.\d+\.\d+$/.test(host) ||
    /^169\.254\.\d+\.\d+$/.test(host) ||
    /^172\.(1[6-9]|2\d|3[01])\.\d+\.\d+$/.test(host)
  ) {
    return true;
  }

  if (host.charAt(0) !== "[") return false;
  const v6 = host.slice(1, host.length - 1);
  return (
    v6 === "::" ||
    v6 === "::1" ||
    v6.indexOf("::ffff:") === 0 ||
    /^f[cd][0-9a-f]{0,2}:/.test(v6) ||
    /^fe[89ab][0-9a-f]?:/.test(v6)
  );
}

/**
 * A cadence a channel actually has, given what the body said and what is
 * stored.
 *
 * Absence is not a value: an older desktop serialises `ChannelSettings` from a
 * Rust struct that has no such field, and reading its silence as `weekly`
 * would turn a user's deliberate `off` back on the next time that build saved
 * anything. Falling back to `base` — the stored row, for every save after the
 * first — is the same rule the rest of this function follows for an
 * unmentioned channel.
 *
 * An unrecognised *string* falls back the same way rather than to a fixed
 * `weekly`: garbage is not a decision either, and the stored choice is a
 * better guess at the user's intent than the product default is.
 */
function cadenceOr(value: unknown, fallback: SummaryCadence): SummaryCadence {
  return SUMMARY_CADENCES.indexOf(value as SummaryCadence) >= 0
    ? (value as SummaryCadence)
    : fallback;
}

/**
 * An IANA zone `Intl` recognises, or null for UTC.
 *
 * Null covers absent, not-a-string, too long, and a name this runtime has
 * never heard of. All four mean the same thing to the scheduler — compute the
 * period in UTC — and none is worth failing a settings save over, because the
 * field is filled in by the UI rather than typed by a person.
 */
function normalizeTimeZone(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > MAX_TIME_ZONE_LENGTH) return null;
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: trimmed });
    return trimmed;
  } catch {
    return null;
  }
}

function clampInt(value: number, min: number, max: number, fallback: number): number {
  if (typeof value !== "number" || !isFinite(value)) return fallback;
  const rounded = Math.round(value);
  if (rounded < min) return min;
  if (rounded > max) return max;
  return rounded;
}

export type NormalizedSettings =
  | { readonly ok: true; readonly settings: NotificationSettings }
  | { readonly ok: false; readonly message: string };

/**
 * Clamp slips, reject decisions.
 *
 * Out-of-range numbers are clamped rather than bounced — same philosophy as
 * the desktop's `AppSettings::normalised` — because a slider past its end is
 * a slip. A bad webhook URL on an enabled channel is rejected instead: quietly
 * "fixing" it would mean alerts silently going nowhere, which is the exact
 * failure mode this whole feature exists to prevent.
 *
 * `base` is what an *absent* field inherits, and callers with a stored row
 * should pass it. It defaults to the shared defaults, which is right for a
 * first save and wrong for every save after: with everything in
 * `DEFAULT_NOTIFICATION_SETTINGS` disabled, a body that simply does not mention
 * Slack used to turn Slack off and return 200 — the same silent suppression
 * `normalizePlanOverride` below refuses to perform on a plan, one level up. A
 * channel the body *does* mention is still read as stated in full, so
 * unchecking a box is a change and omitting a key is not.
 */
export function normalizeNotificationSettings(
  input: NotificationSettings,
  base: NotificationSettings = DEFAULT_NOTIFICATION_SETTINGS,
): NormalizedSettings {
  const channels: Record<AlertChannelKind, ChannelSettings> = {
    ...base.channels,
  };

  for (const kind of ALL_CHANNEL_KINDS) {
    const given = input.channels ? input.channels[kind] : undefined;
    const fallback = base.channels[kind];
    if (!given) {
      channels[kind] = fallback;
      continue;
    }

    const enabled = given.enabled === true;
    const minFloor = kind === "email" ? 1 : 0;
    const minIntervalDays = clampInt(
      given.minIntervalDays,
      minFloor,
      MAX_INTERVAL_DAYS,
      fallback.minIntervalDays,
    );

    const summaryCadence = cadenceOr(given.summaryCadence, fallback.summaryCadence ?? "weekly");

    if (kind === "email") {
      channels[kind] = { enabled, minIntervalDays, summaryCadence };
      continue;
    }

    const webhookUrl = typeof given.webhookUrl === "string" ? given.webhookUrl.trim() : "";
    if (enabled) {
      const problem = webhookUrlProblem(webhookUrl);
      if (problem !== null) {
        return { ok: false, message: `${channelLabel(kind)}: ${problem}` };
      }
    } else if (webhookUrl.length > MAX_WEBHOOK_URL_LENGTH) {
      return { ok: false, message: `${channelLabel(kind)}: that URL is too long.` };
    }

    channels[kind] = {
      enabled,
      minIntervalDays,
      webhookUrl,
      notifyOnSuccess: given.notifyOnSuccess === true && minIntervalDays === 0,
      summaryCadence,
    };
  }

  return {
    ok: true,
    settings: {
      staleAfterDays: clampInt(
        input.staleAfterDays,
        MIN_STALE_AFTER_DAYS,
        MAX_STALE_AFTER_DAYS,
        base.staleAfterDays,
      ),
      // Absent inherits, like everything else here: a client that does not know
      // the field must not move an account it never saw to UTC.
      timeZone:
        input.timeZone === undefined ? (base.timeZone ?? null) : normalizeTimeZone(input.timeZone),
      channels,
    },
  };
}

/** Clamp a per-plan override into range; nulls (inherit) pass through. */
export function normalizePlanOverride(
  input: PlanNotificationOverride,
): PlanNotificationOverride {
  // An absent field means "inherit", exactly as an explicit null does — a
  // partial body must never mute a plan it did not mention. (Coercing a
  // missing `alertOnFailure` to false is precisely the silent-suppression
  // failure this feature exists to prevent.) All three fields agree on this.
  return {
    alertOnFailure: input.alertOnFailure == null ? null : input.alertOnFailure === true,
    alertOnSuccess: input.alertOnSuccess == null ? null : input.alertOnSuccess === true,
    staleAfterDays:
      input.staleAfterDays == null
        ? null
        : clampInt(
            input.staleAfterDays,
            MIN_STALE_AFTER_DAYS,
            MAX_STALE_AFTER_DAYS,
            MIN_STALE_AFTER_DAYS,
          ),
  };
}

/** An all-null override inherits everything and should not be stored. */
export function overrideInheritsEverything(o: PlanNotificationOverride): boolean {
  return o.alertOnFailure === null && o.alertOnSuccess === null && o.staleAfterDays === null;
}

/** Display name for a cadence, so both UIs and the message footer agree. */
export function summaryCadenceLabel(cadence: SummaryCadence): string {
  switch (cadence) {
    case "off":
      return "Never";
    case "daily":
      return "Daily";
    case "weekly":
      return "Weekly";
    case "monthly":
      return "Monthly";
  }
}

/** When a cadence actually delivers, for the hint beside the picker. */
export function summaryCadenceHint(cadence: SummaryCadence): string {
  switch (cadence) {
    case "off":
      return "No status summaries on this channel.";
    case "daily":
      return "A status report every morning, covering the previous day.";
    case "weekly":
      return "A status report every Monday morning, covering the previous week.";
    case "monthly":
      return "A status report on the 1st of each month, covering the previous month.";
  }
}

/** Display name for a channel, used in UI labels and error messages. */
export function channelLabel(kind: AlertChannelKind): string {
  switch (kind) {
    case "email":
      return "Email";
    case "slack":
      return "Slack";
    case "discord":
      return "Discord";
    case "webhook":
      return "Webhook";
  }
}
